import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { all_routes } from "router/all_routes";
import { getPayrollById } from "../../../services/payrollService";
import { Payroll, PayrollLine } from "../../../types/payrollTypes";
import { formatMoney } from "helpers/numberHelper";
import { formatToLocaleDate } from "utils/dateUtils";
import usePayroll from "../hooks/usePayroll";
import Loader from "components/Common/loader";
import PayrollStatusBadge from "components/Common/Badge/PayrollStatusBadge";
import CustomButton from "components/Common/ui/CustomButton";
import PayrollLineModal from "../components/PayrollLineModal";
import PayrollPdf from "../components/PayrollPdf";
import Datatable from "components/Common/dataTable";
import PageHeader from "components/Common/PageHeader";
import { useAbility } from "config/abilityContext";
import Badge from "components/Common/ui/Badge";
import NoDataComponent from "components/Common/NoData";

const PayrollDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { idPayroll } = useParams<{ idPayroll: string }>();
  const navigate = useNavigate();
  const [payroll, setPayroll] = useState<Payroll | null>(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const ability = useAbility();
  const [searchParams, setSearchParams] = useSearchParams();
  const showPdfModal = searchParams.get("showPdf");


  const showAddLineModal = () => {
    setIsModalOpen(true);
  };

  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    ability.can("read", "Payroll") ? { label: "payroll.my_payrolls", path: all_routes.myPayroll } : { label: "payroll.title", path: all_routes.payroll },
    { label: "payroll.details", active: true },
  ];
  const { payrollDetail, handleAddPayrollLine } = usePayroll();

  useEffect(() => {
    setLoading(true);
    loadPayrollDetail();
    if (showPdfModal == "true") {
      setShowPdf(true);
    }
  }, [idPayroll]);

  const loadPayrollDetail = useCallback(() => {
    if (idPayroll) {
      payrollDetail(idPayroll).then((res) => {
        setPayroll(!res ? null : res);
        setLoading(false);
      });
    }
  }, [idPayroll]);



  const saveLine = async (body: any) => {
    const response = await handleAddPayrollLine({ payrollId: idPayroll, ...body });
    if (response) {
      loadPayrollDetail();
      setIsModalOpen(false);
      return true;
    }
    return false;
  };

  const payrollLineColumns = [
    {
      title: t("payroll.form.line_type"),
      dataIndex: "lineType",
      key: "lineType",
      render: (lineType: string) => (
        <Badge
          showIcon={false}
          variant={lineType == 'bonus' ? 'success' : lineType == 'deduction' ? 'danger' : 'primary'}
          text={t(`payroll.lineType.${lineType}`)}
        />

      ),
    },
    {
      title: t("payroll.form.source_type"),
      dataIndex: "sourceType",
      key: "sourceType",
      render: (sourceType: string) => t(`payroll.sourceType.${sourceType}`),
    },
    {
      title: t("payroll.form.amount"),
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => formatMoney(amount),
    },
    {
      title: t("payroll.form.status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <PayrollStatusBadge status={status as any} />
      ),
    },
  ];

  if (loading) {
    return <Loader />;
  }

  if (!payroll) {
    navigate(all_routes.noAccess);
    return null;
  }


  if (showPdf) {
    return <PayrollPdf payroll={payroll as any} onBack={() => setShowPdf(false)} />;
  }

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("payroll.details")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex justify-content-end mb-2">
          <CustomButton
            onClick={() => setShowPdf(true)}
            className=""
          >
            <i className="ti ti-pdf me-2" />
            {t("payroll.pdf")}
          </CustomButton>
        </div>



        <div className="row">
          <div className="col-lg-8">
            {/* Payroll Information Card */}
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">{t("payroll.payroll_information")}</h5>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th className="w-25">{t("payroll.form.staff")}</th>
                        <td>{payroll.staff ? `${payroll.staff?.user?.firstName} ${payroll.staff?.user?.lastName}` : '-'}</td>
                      </tr>
                      <tr>
                        <th>{t("payroll.form.period")}</th>
                        <td>{formatToLocaleDate(payroll.startDate)} - {formatToLocaleDate(payroll.endDate)}</td>
                      </tr>
                      <tr>
                        <th>{t("payroll.form.status")}</th>
                        <td>
                          <PayrollStatusBadge status={payroll.computedStatus as any} />
                        </td>
                      </tr>
                      <tr>
                        <th>{t("payroll.form.created_at")}</th>
                        <td>{payroll.createdAt ? formatToLocaleDate(payroll.createdAt) : '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            {/* Amount Summary Card */}
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">{t("payroll.amount_summary")}</h5>
              </div>
              <div className="card-body">
                <div className="amount-summary">
                  <div className="d-flex justify-content-between mb-3">
                    <strong>{t("payroll.form.gross_amount")}:</strong>
                    <span className="text-primary">{formatMoney(payroll.grossAmount)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3">
                    <strong>{t("payroll.form.net_amount")}:</strong>
                    <span className="text-success">{formatMoney(payroll.netAmount)}</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>{t("payroll.form.total_lines")}:</strong>
                    <span>{payroll.payrollLines?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payroll Lines Card */}
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">{t("payroll.payroll_lines")}</h5>
            {ability.can("create", "Payroll") && payroll.status === "pending" && (
              <CustomButton onClick={showAddLineModal}>
                <i className="fa fa-plus me-2"></i>
                {t("payroll.add_line")}
              </CustomButton>
            )}
          </div>
          <div className="card-body">
            <Datatable
              columns={payrollLineColumns}
              dataSource={payroll.payrollLines || []}
              rowKey="id"
              pagination={false}
            />
          </div>
        </div>

        <PayrollLineModal
          visible={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={saveLine}
        />
      </div>
    </div>
  );
};

export default PayrollDetailPage;